import React, { HTMLProps, useState } from "react";
import { Avatar, AvatarProps, Skeleton } from "@mui/material";
import { getAlphabetImageSrc } from "../alphabet-images";

export function CoreProfileImage({
	src,
	name,
	isLoading: externalLoading,
	size = 32,
	style,
	...imgProps
}: {
	name?: string;
	isLoading?: boolean;
	size?: number | string;
} & Omit<HTMLProps<HTMLImageElement>, "width" | "height" | "onLoad" | "onError">) {
	const [imageLoading, setImageLoading] = useState(true);
	const [isError, setIsError] = useState(false);

	const isLoading = externalLoading || (src && imageLoading);
	const isLoaded = src && !isLoading && !isError;

	const sharedStyles = {
		width: size,
		height: size,
		borderRadius: "50%",
	};

	return (
		<>
			{isLoading && <Skeleton variant="circular" width={size} height={size} />}
			{src && <img
				{...imgProps}
				src={src}
				onLoad={() => setImageLoading(false)}
				onError={() => {
					setImageLoading(false);
					setIsError(true);
				}}
				style={{
					...style,
					...sharedStyles,
					display: isLoaded ? undefined : "none",
				}}
			/>}
			{!isLoading && !isLoaded && (
				<img {...imgProps} src={getAlphabetImageSrc(name)} style={{ ...style, ...sharedStyles }} />
			)}
		</>
	);
}

type CoreAvatarColor = "green" | "pink" | "brown" | "purple" | "olive";

const CoreAvatarColorConfig: Record<CoreAvatarColor, { color: string; background: string; border: string }> = {
	brown: {
		border: "#915A46",
		color: "#75331D",
		background: "#F9EEE8",
	},
	pink: {
		border: "#C460D1",
		color: "#6F1678",
		background: "#F5E2F8",
	},
	green: {
		border: "#657349",
		color: "#4B582E",
		background: "#F4F6EE",
	},
	purple: {
		border: "#831C8D",
		color: "#6F1678",
		background: "#F5E2F8",
	},
	olive: {
		border: "#7A6644",
		color: "#895A1C",
		background: "#F8F0E2",
	},
};

const isCoreAvatarColor = (color: string): color is CoreAvatarColor => {
	return color in CoreAvatarColorConfig;
};

type CoreAvatarProps = {
	width?: string | number;
	height?: string | number;
	children: React.ReactNode;
	colorConfig?: CoreAvatarColor | number;
	borderColor?: string;
	textColor?: string;
	bgColor?: string;
} & AvatarProps;

export function CoreAvatar({
	children,
	width = 24,
	height = 24,
	borderColor,
	textColor,
	bgColor,
	colorConfig,
	...props
}: CoreAvatarProps) {
	const finalWidth = typeof width === "number" ? `${width}px` : width;
	const finalHeight = typeof height === "number" ? `${height}px` : height;
	const colorKeys = Object.keys(CoreAvatarColorConfig) as CoreAvatarColor[];
	const finalColorConfig =
		typeof colorConfig === "number"
			? CoreAvatarColorConfig[colorKeys[colorConfig % colorKeys.length]]
			: colorConfig && typeof colorConfig === "string" && isCoreAvatarColor(colorConfig)
				? CoreAvatarColorConfig[colorConfig]
				: CoreAvatarColorConfig["green"];
	const finalBorderColor = borderColor ?? finalColorConfig["border"];
	const finalBgColor = bgColor ?? finalColorConfig["background"];
	const finalTextColor = textColor ?? finalColorConfig["color"];

	return (
		<Avatar
			sx={{
				width: finalWidth,
				height: finalHeight,
				color: finalTextColor,
				backgroundColor: finalBgColor,
				border: `1px solid ${finalBorderColor}`,
				...props.sx,
			}}
			{...props}
		>
			{children}
		</Avatar>
	);
}
